import { ERROR_QUERY_PARAM, SESSION_PREV_ROUTE } from 'utils/constants';
import { isNotProduction, isNotStage } from 'utils/isNotProduction';
import { isTinkRejectedByMessage } from 'utils/constants/constants_tinkStelo';
import { Navigate, useLocation } from 'react-router-dom';
import { PropsWithChildren, useEffect, useState } from 'react';
import { ROUTES } from 'utils/constants/constants_routes';
import { trackingNewAvailableRoute } from 'utils/routeTracking';
import { useGetClientApplication } from 'api/atoms';
import NoBEValidationsBanner from 'components/Router/NoBEValidationsBanner/NoBEValidationsBanner';
import useClient from 'hooks/useClient';
import useClientBlockedStatus from 'hooks/useClientBlockedStatus';

export default function GuardRoute({
  children,
  authenticated,
}: PropsWithChildren<{
  available?: boolean;
  authenticated?: boolean;
}>) {
  const [availableFromBEValidations, setAvailableFromBEValidations] = useState({
    path: window.location.pathname,
    valid: true,
  });
  const { clientBlockedStatus } = useClientBlockedStatus();
  const { data: application } = useGetClientApplication();
  const { client } = useClient();
  const { pathname: urlPath } = useLocation();

  const searchParams = new URLSearchParams(window.location.search);

  const readyForNextScreen =
    authenticated !== true ||
    (Boolean(client) && Boolean(application) && Boolean(clientBlockedStatus));

  const isRouteAvailableAccordingtoBE = () => {
    if (!client || !application) return true;

    const isResolutionRejectedCancelled =
      client?.blocked ||
      application?.resolution === 'REJECTED' ||
      application?.resolution === 'CANCELLED';

    const isResolutionApproved = application.resolution === 'APPROVED';

    const errorReasonExists = Boolean(searchParams.get(ERROR_QUERY_PARAM));

    switch (urlPath) {
      case ROUTES.FIRST_LOAN:
      case ROUTES.FIRST_LOAN_EMAIL:
        return !client?.email;
      case ROUTES.FIRST_LOAN_CONFIRMATION:
        return Boolean(client.email) && !client?.dateOfBirth;
      case ROUTES.FIRST_LOAN_PERSONAL_DATA:
        return !client?.dateOfBirth;
      case ROUTES.FIRST_LOAN_ADDRESS:
        return (
          Boolean(client?.dateOfBirth) && !client?.declaredAddress?.postalCode
        );
      case ROUTES.FIRST_LOAN_INCOME:
        return Boolean(
          client?.declaredAddress?.postalCode &&
            !client?.employer?.employmentStatus,
        );
      case ROUTES.FIRST_LOAN_PHONE_VERIFICATION:
      case ROUTES.FIRST_LOAN_INCORRECT_CODE:
      case ROUTES.FIRST_LOAN_PHONE_VERIFICATION_CHANGE_PHONE:
        return Boolean(
          client?.employer?.employmentStatus ||
            application?.affiliateCooperationType === 'API',
        );
      case ROUTES.FIRST_LOAN_PHONE_VERIFICATION_WAITING:
        return Boolean(
          client?.employer?.employmentStatus ||
            application?.affiliateCooperationType === 'API',
        );
      case ROUTES.FIRST_LOAN_ONLINE_BANK_VERIFY:
      case ROUTES.FIRST_LOAN_ONLINE_BANK_VERIFY_NEW:
      case ROUTES.FIRST_LOAN_ONLINE_BANK_ERROR:
      case ROUTES.FIRST_LOAN_ONLINE_BANK_CHECK:
      case ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS:
      case ROUTES.FIRST_LOAN_TINK_BANK_NA:
      case ROUTES.FIRST_LOAN_TINK_BANK_ISSUE:
      case ROUTES.FIRST_LOAN_TINK_LATER:
        return Boolean(
          (application?.confirmed || application.resolution === 'REJECTED') &&
            !isResolutionApproved,
        );
      case ROUTES.FIRST_LOAN_MANUAL_UPLOAD:
        return Boolean(application?.confirmed && !isResolutionApproved);
      case ROUTES.FIRST_LOAN_MANUAL_UPLOAD_BANK_ACCOUNT:
        return Boolean(client.hasAttachments);
      case ROUTES.FIRST_LOAN_ONLINE_BANK_IBAN:
        return Boolean(client.hasAttachments);
      case ROUTES.FIRST_LOAN_MANUAL_UPLOAD_WAITING:
        return Boolean(client.hasAttachments && client.bankAccount);
      case ROUTES.FIRST_LOAN_ONLINE_BANK_PENDING_WAITING:
        return Boolean(client.bankAccount && client.hasAttachments);
      case ROUTES.FIRST_LOAN_ONLINE_BANK_REJECTED_BY_TINK:
        return Boolean(
          isResolutionRejectedCancelled &&
            isTinkRejectedByMessage(application?.resolutionMessage),
        );
      case ROUTES.FIRST_LOAN_REJECTED:
        return (
          isResolutionRejectedCancelled || client?.blocked || errorReasonExists
        );
      case ROUTES.ACCOUNT_REPEATED_LOAN_CONFIRM:
      case ROUTES.ACCOUNT_REPEATED_LOAN_WAITING:
        return !application.confirmed;
      case ROUTES.ACCOUNT_REPEATED_LOAN_REJECTED_APPLICATION:
      case ROUTES.ACCOUNT_REPEATED_LOAN_APPROVED_APPLICATION:
        return Boolean(application.confirmed);
      case ROUTES.AFFILIATE_FIRST_LOAN_APPLICATION:
        return Boolean(
          application?.affiliateCooperationType === 'API' &&
            application?.status === 'OPEN' &&
            !application?.confirmed,
        );
      case ROUTES.PAYMENT_SUCCESS:
      case ROUTES.PAYMENT_FAILED:
      case ROUTES.FIRST_LOAN_ONLINE_BANK_APPROVED:
        return !isResolutionRejectedCancelled;
      case ROUTES.BEST_OFFER:
        return application.resolutionDetail === 'NEW_OFFER';
      case ROUTES.REJECTED_TIMER:
        return clientBlockedStatus?.blocked;
      case ROUTES.FIRST_LOAN_WELCOME:
        return !clientBlockedStatus?.blocked;
      case ROUTES.STELO_APPROVED:
        return application?.resolution === 'APPROVED';
      case ROUTES.STELO_REJECTED:
      case ROUTES.STELO_REJECTED_TINK:
        return application?.resolution === 'REJECTED';
      case ROUTES.ONE_TIME_TOKEN_LOGIN_WAITING:
      case ROUTES.FIRST_LOAN_ONLINE_BANK_TINK_WAITING:
        return true;
      default:
        return undefined;
    }
  };

  const isAuthenticated = Boolean(sessionStorage.getItem('token'));

  const userAuthenticationValided =
    authenticated === undefined || authenticated === isAuthenticated;

  // useEffect to check BE properties to know if route is available
  useEffect(() => {
    const checkConditions = async () => {
      const nextAvailable = isRouteAvailableAccordingtoBE();
      if (readyForNextScreen && nextAvailable && userAuthenticationValided) {
        trackingNewAvailableRoute({ client, application });
        sessionStorage.setItem(SESSION_PREV_ROUTE, urlPath);
      }
      if (nextAvailable !== availableFromBEValidations?.valid)
        setAvailableFromBEValidations({
          path: urlPath,
          valid: Boolean(nextAvailable),
        });
    };
    checkConditions();
  }, [readyForNextScreen, urlPath, client, application]);

  const safestRoute =
    sessionStorage.getItem(SESSION_PREV_ROUTE) || ROUTES.FIRST_LOAN;

  // Navigate to the last safest route
  const isValidSafestRoute =
    safestRoute !== urlPath &&
    safestRoute !== ROUTES.ONE_TIME_TOKEN_LOGIN_WAITING;

  // Redirect to initial Route (queryParam added only for tracking)
  if (urlPath === '/') {
    return <Navigate to={ROUTES.FIRST_LOAN + '?gr=true'} />;
  }

  // Navigate to the last safest route
  const isInvalidCurrentRoute =
    !availableFromBEValidations?.valid &&
    availableFromBEValidations?.path === urlPath;
  if (
    isValidSafestRoute &&
    (isInvalidCurrentRoute || !userAuthenticationValided)
  ) {
    if (safestRoute !== urlPath) {
      if (safestRoute === ROUTES.FIRST_LOAN) sessionStorage.clear();
      return <Navigate to={safestRoute} />;
    }
  }

  // Show unsafe banner in case route is not on "isRouteAvailableAccordingtoBE" (this only applies on NON PROD environments)
  const showUnsafeRouteBanner =
    availableFromBEValidations?.valid === undefined &&
    isNotProduction() &&
    isNotStage();

  return (
    <>
      {showUnsafeRouteBanner && <NoBEValidationsBanner />}
      {children}
    </>
  );
}
